import { useWorkspaceStore } from '@/features/workspace/hooks/workspaceStore';
import {
    Button,
    Group,
    Modal,
    Stack,
    TextInput,
    Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useCreateWorkspace } from '../../services/useWorkspaceQueries';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface CreateWorkspaceDialogProps {
    opened: boolean;
    onClose: () => void;
}

export function CreateWorkspaceDialog({
    opened,
    onClose,
}: CreateWorkspaceDialogProps) {
    const { t } = useTranslation('workspace');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { mutateAsync: createWorkspace } = useCreateWorkspace();
    const navigate = useNavigate();
    const fetchWorkspaces = useWorkspaceStore(state => state.fetchWorkspaces);

    const form = useForm({
        initialValues: {
            name: '',
            description: '',
        },
        validate: {
            name: (value: string) =>
                value.trim().length === 0
                    ? t('component.create_dialog.name_required')
                    : null,
        },
    });

    const handleSubmit = async (values: typeof form.values) => {
        setIsSubmitting(true);
        try {
            const createdWorkspace = await createWorkspace({
                name: values.name,
                description: values.description,
            });

            notifications.show({
                title: t('component.create_dialog.success_title'),
                message: t('component.create_dialog.success_message'),
                color: 'green',
            });

            // 워크스페이스 목록 다시 불러오기
            await fetchWorkspaces();

            form.reset();

            // 생성한 워크스페이스 페이지로 리다이렉트
            void navigate(`/workspace/${createdWorkspace.id}/setting`);

            onClose();
        } catch (error) {
            notifications.show({
                title: t('component.create_dialog.error_title'),
                message:
                    error instanceof Error
                        ? error.message
                        : t('component.create_dialog.error_message'),
                color: 'red',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('component.create_dialog.title')}
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack>
                    <TextInput
                        label={t('component.create_dialog.name_label')}
                        placeholder={t('component.create_dialog.name_placeholder')}
                        required
                        {...form.getInputProps('name')}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button variant="outline" onClick={onClose}>
                            {t('component.create_dialog.cancel_button')}
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            {t('component.create_dialog.create_button')}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}
